import { InlineSkeleton } from '@/components/loading/Skeleton'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Ellipsis, Expand, Globe, Info, Lock, Minimize, MonitorDown, QrCode, Star, Volume2, VolumeX, type LucideIcon } from 'lucide-react'
import { Popover, Tooltip, type PopoverProps } from '@/components/play'
import { CameraPicture, CameraStatus, useCameraState } from './WorkspaceMedia'
import { CAMERA_STATE_LABELS } from '@/lib/workspace'
import { cn } from '@/lib/utils'
import type { StreamResource } from '@/types/events'
import './workspace-video.css'

export interface VideoControlsProps {
  stream: StreamResource
  featured?: boolean
  number?: number
  showName?: boolean
  onPreview?: () => void
  onDetails?: () => void
  onVisibilityChange?: (visible: boolean) => Promise<void>
  onFeature?: () => void | Promise<void>
  featureBusy?: boolean
  canFeature?: boolean
  featureDisabledReason?: string
  onSetup?: () => void
  info?: ReactNode
  audio?: boolean
  audioAvailable?: boolean
  onToggleAudio?: () => void
  fullscreen?: boolean
  fullscreenBusy?: boolean
  onFullscreen?: () => void
  fullscreenAvailable?: boolean
  portalContainer?: HTMLElement | null
  className?: string
}

export interface WorkspaceVideoProps extends VideoControlsProps {
  selected?: boolean
  onSelect?: () => void
}

function sourceName(stream: StreamResource) {
  return stream.attributes.source_role === 'programme' ? 'Live show' : stream.attributes.name
}

function privacyExplanation(visible: boolean) {
  return visible
    ? 'Available on the public event page when it’s enabled.'
    : 'Hidden from the public event page. Available in your workspace and OBS.'
}

function useVideoPortal(streamId: string, override?: HTMLElement | null) {
  const ref = useRef<HTMLDivElement>(null)
  const [stage, setStage] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setStage(ref.current?.closest<HTMLElement>('[data-fullscreen-root]') || null)
  }, [streamId])
  return { ref, container: override || stage }
}

/** Video controls are named by their icons and labels; the tooltip explains, so it opens sooner than the Play default. */
const VIDEO_TOOLTIP_DELAY = 250

function VideoAction({ label, help = label, icon: Icon, onClick, disabled, pressed, busy, container }: {
  label: string
  help?: ReactNode
  icon: LucideIcon
  onClick: () => void
  disabled?: boolean
  pressed?: boolean
  busy?: boolean
  container?: HTMLElement | null
}) {
  const reasonId = useId()
  // A disabled action keeps its wrapper focusable so the tooltip can say why. The Play tooltip is visual only,
  // so the wrapper is also described by that reason for screen readers.
  const reason = disabled && help !== label
  return (
    <Tooltip content={help} container={container} delay={VIDEO_TOOLTIP_DELAY} className="pw-video-tooltip">
      <span className="pw-video-control-wrap" tabIndex={disabled ? 0 : undefined} aria-describedby={reason ? reasonId : undefined}>
        <button type="button" className="pw-video-control" aria-label={label} aria-pressed={pressed} aria-busy={busy || undefined} disabled={disabled} onClick={onClick}>
          {busy ? <InlineSkeleton label={label} /> : <Icon size={18} aria-hidden />}
        </button>
        {reason && <span id={reasonId} hidden>{help}</span>}
      </span>
    </Tooltip>
  )
}

function VideoIndicator({ label, children, icon: Icon, container }: {
  label: string
  children: ReactNode
  icon: LucideIcon
  container?: HTMLElement | null
}) {
  return (
    <Popover
      openOnHover
      label={label}
      side="bottom"
      align="end"
      container={container}
      className="pw-video-indicator-help"
      trigger={
        <button type="button" className="pw-video-indicator" aria-label={label}>
          <span><Icon size={14} aria-hidden /></span>
        </button>
      }
    >
      {children}
    </Popover>
  )
}

export function VideoIndicators({ stream, featured, portalContainer }: Pick<VideoControlsProps, 'stream' | 'featured' | 'portalContainer'>) {
  const { ref, container } = useVideoPortal(stream.id, portalContainer)
  const name = sourceName(stream)
  return (
    <div ref={ref} className="pw-video-indicators">
      {stream.attributes.publicly_visible === false && <VideoIndicator label={`Private camera: ${name}`} icon={Lock} container={container}>{privacyExplanation(false)}</VideoIndicator>}
      {featured && <VideoIndicator label={`Featured: ${name}`} icon={Star} container={container}>Featured on the public event page.</VideoIndicator>}
    </div>
  )
}

function VideoOptions({ stream, onVisibilityChange, featured, onFeature, featureBusy, canFeature, featureDisabledReason, onDetails, onSetup, info, portalContainer }: VideoControlsProps) {
  const name = sourceName(stream)
  const visible = stream.attributes.publicly_visible !== false
  const toolbarId = useId()
  const trigger = useRef<HTMLButtonElement>(null)
  const toolbar = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>()
  const suppressFocusOpen = useRef(false)
  const focused = useRef(false)
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [saving, setSaving] = useState(false)
  const [featuring, setFeaturing] = useState(false)
  const [error, setError] = useState('')
  const pending = useRef(false)
  const featurePending = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      clearTimeout(closeTimer.current)
    }
  }, [])

  function close() {
    clearTimeout(closeTimer.current)
    setPinned(false)
    setOpen(false)
    setShowInfo(false)
  }
  // The button opens the toolbar itself, so the popover doesn't manage it. Like any dismissable layer,
  // focus landing outside the button and the toolbar closes it.
  useEffect(() => {
    if (!open) return
    const doc = trigger.current?.ownerDocument ?? document
    function closeOnFocusOutside(event: FocusEvent) {
      if (event.target instanceof Node && !trigger.current?.contains(event.target) && !toolbar.current?.contains(event.target)) close()
    }
    doc.addEventListener('focusin', closeOnFocusOutside)
    return () => doc.removeEventListener('focusin', closeOnFocusOutside)
  }, [open])
  const dismiss: NonNullable<PopoverProps['onOpenChange']> = (next, details) => {
    if (next) return
    const target = details.reason === 'focus-out' ? (details.event as FocusEvent).relatedTarget : details.event.target
    if ((details.reason === 'outside-press' || details.reason === 'focus-out') && target instanceof Node && trigger.current?.contains(target)) {
      details.cancel()
      return
    }
    if (details.reason === 'escape-key') returnFocus()
    close()
  }
  function returnFocus() {
    focused.current = false
    const button = trigger.current
    if (!button || button === button.ownerDocument.activeElement) return
    suppressFocusOpen.current = true
    button.focus()
  }
  function enter() {
    clearTimeout(closeTimer.current)
    setOpen(true)
  }
  function leave() {
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => {
      if (!pinned && !focused.current && !pending.current && !featurePending.current) setOpen(false)
    }, 180)
  }
  function blur(next: EventTarget | null) {
    if (next instanceof Node && (toolbar.current?.contains(next) || trigger.current?.contains(next))) return
    focused.current = false
    leave()
  }
  function focusToolbar() {
    setPinned(true)
    setOpen(true)
    requestAnimationFrame(() => toolbar.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus())
  }
  async function run(kind: 'privacy' | 'feature') {
    const active = kind === 'privacy' ? pending : featurePending
    if (active.current || (kind === 'privacy' ? !onVisibilityChange : !onFeature || featureBusy || !canFeature)) return
    active.current = true
    setPinned(true)
    setError('')
    if (kind === 'privacy') setSaving(true)
    else setFeaturing(true)
    try {
      if (kind === 'privacy') await onVisibilityChange?.(!visible)
      else await onFeature?.()
      if (mounted.current) close()
    } catch {
      if (mounted.current) {
        setOpen(true)
        setError(kind === 'privacy' ? 'Privacy couldn’t be updated. Try again.' : 'Featuring couldn’t be updated. Try again.')
      }
    } finally {
      active.current = false
      if (mounted.current) {
        if (kind === 'privacy') setSaving(false)
        else setFeaturing(false)
      }
    }
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="pw-video-control pw-video-more"
        aria-label={`Video controls for ${name}`}
        aria-expanded={open}
        aria-controls={toolbarId}
        aria-haspopup="dialog"
        onPointerEnter={(event) => { if (event.pointerType !== 'touch') enter() }}
        onPointerLeave={leave}
        onFocus={() => {
          focused.current = true
          if (suppressFocusOpen.current) suppressFocusOpen.current = false
          else enter()
        }}
        onBlur={(event) => blur(event.relatedTarget)}
        onClick={(event) => {
          if (pinned) close()
          else if (event.detail === 0) focusToolbar()
          else { setPinned(true); enter() }
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') { event.preventDefault(); focusToolbar() }
          if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); close() }
        }}
      >
        <Ellipsis size={20} aria-hidden />
      </button>
      <Popover
        ref={toolbar}
        id={toolbarId}
        anchor={trigger}
        open={open}
        onOpenChange={dismiss}
        label={`Video controls for ${name}`}
        side="top"
        align="end"
        container={portalContainer}
        className="pw-video-toolbar-popover"
        initialFocus={false}
        finalFocus={false}
        // Capture, so the open tooltip of the focused action doesn't take the Escape that closes the toolbar.
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            returnFocus()
            close()
          }
        }}
        onPointerEnter={enter}
        onPointerLeave={leave}
        onFocusCapture={() => { focused.current = true; clearTimeout(closeTimer.current) }}
        onBlurCapture={(event) => blur(event.relatedTarget)}
      >
        <div className="pw-video-expanded-actions" role="toolbar" aria-label={`More actions for ${name}`}>
          <VideoAction label={onVisibilityChange ? saving ? 'Saving…' : visible ? 'Make private' : 'Make public' : 'Privacy'} help={<><strong>{onVisibilityChange ? visible ? 'Make private' : 'Make public' : 'Privacy'}</strong><br />{privacyExplanation(visible)}</>} icon={visible ? Globe : Lock} busy={saving} disabled={saving} onClick={() => onVisibilityChange ? void run('privacy') : setShowInfo((value) => !value)} container={portalContainer} />
          {onFeature && <VideoAction label={featured ? `Stop featuring ${name}` : `Feature ${name}`} help={!canFeature && featureDisabledReason ? featureDisabledReason : featured ? 'Stop featuring on the event page' : 'Feature on the public event page'} icon={Star} pressed={featured} busy={featuring || featureBusy} disabled={featuring || featureBusy || !canFeature} onClick={() => void run('feature')} container={portalContainer} />}
          {onDetails && <VideoAction label={`Details for ${name}`} help="Camera invitation and details" icon={QrCode} onClick={() => { close(); onDetails() }} container={portalContainer} />}
          {onSetup && <VideoAction label="OBS setup" icon={MonitorDown} onClick={() => { close(); onSetup() }} container={portalContainer} />}
          {info && <VideoAction label={`About ${name}`} help={info} icon={Info} pressed={showInfo} onClick={() => { setPinned(true); setShowInfo((value) => !value) }} container={portalContainer} />}
        </div>
        {showInfo && <div className="pw-video-toolbar-info">{info || privacyExplanation(visible)}</div>}
        {error && <p className="pw-video-toolbar-error" role="alert">{error}</p>}
      </Popover>
    </>
  )
}

export function VideoControls({ stream, featured = false, number, showName = true, onPreview, onDetails, onVisibilityChange, onFeature, featureBusy = false, canFeature = true, featureDisabledReason, onSetup, info, audio = false, audioAvailable = false, onToggleAudio, fullscreen = false, fullscreenBusy = false, onFullscreen, fullscreenAvailable = true, portalContainer, className }: VideoControlsProps) {
  const name = sourceName(stream)
  const isShow = stream.attributes.source_role === 'programme'
  const { ref, container } = useVideoPortal(stream.id, portalContainer)
  return (
    <div ref={ref} className={cn('pw-video-controls', className)}>
      {(showName || number !== undefined) && <div className="pw-video-identity">
        {number !== undefined && !isShow && <span className="pw-video-number" aria-hidden>{String(number).padStart(2, '0')}</span>}
        {showName && <h2 title={name}>{name}</h2>}
      </div>}
      <div className="pw-video-actions" role="group" aria-label={`Actions for ${name}`}>
        <VideoOptions key={stream.id} stream={stream} onVisibilityChange={onVisibilityChange} onFeature={onFeature} featured={featured} featureBusy={featureBusy} canFeature={canFeature} featureDisabledReason={featureDisabledReason} onDetails={onDetails} onSetup={onSetup} info={info} portalContainer={container} />
        {onToggleAudio && <VideoAction label={audio ? 'Mute preview' : 'Listen to preview'} help={audioAvailable ? audio ? 'Mute preview' : 'Listen to preview' : 'No audio available'} icon={audio ? Volume2 : VolumeX} onClick={onToggleAudio} pressed={audio} disabled={!audioAvailable} container={container} />}
        {onFullscreen ? <VideoAction label={fullscreen ? 'Exit fullscreen' : 'View fullscreen'} help={fullscreenAvailable ? fullscreen ? 'Exit fullscreen' : 'View fullscreen' : 'Fullscreen is not supported here'} icon={fullscreen ? Minimize : Expand} onClick={onFullscreen} pressed={fullscreen} disabled={fullscreenBusy || !fullscreenAvailable} busy={fullscreenBusy} container={container} /> : onPreview && <VideoAction label={`Expand ${name}`} icon={Expand} onClick={onPreview} container={container} />}
      </div>
    </div>
  )
}

export function WorkspaceVideo({ selected = false, onSelect, className, ...props }: WorkspaceVideoProps) {
  const { stream, featured, portalContainer } = props
  const state = useCameraState(stream)
  const name = sourceName(stream)
  const isShow = stream.attributes.source_role === 'programme'
  const statusLabel = isShow && state === 'invited' ? 'Waiting for OBS' : CAMERA_STATE_LABELS[state]
  return (
    <div className={cn('pw-workspace-video', className)}>
      <div className={cn('pw-workspace-picture', selected && 'pw-workspace-picture--selected')}>
        <CameraPicture stream={stream} />
        {(onSelect || props.onPreview) && <button type="button" className="pw-video-select" aria-label={`${onSelect ? 'Select' : 'Preview'} ${name}`} aria-pressed={onSelect ? selected : undefined} onClick={onSelect || props.onPreview} />}
        <div className="pw-video-top">
          <div className="pw-video-status" title={statusLabel}><CameraStatus state={state} label={statusLabel} /></div>
          <VideoIndicators stream={stream} featured={featured} portalContainer={portalContainer} />
        </div>
      </div>
      <VideoControls {...props} />
    </div>
  )
}
